/* A query error is an ApolloError wrapping the hub-http error as
 * `networkError`; a raw hub-http error can also be thrown directly. Either way
 * the request details live on the inner error, so read from that when present. */
const getRequestError = error => {
  if ('networkError' in error && typeof error.networkError === 'object' && error.networkError !== null) {
    return error.networkError;
  }
  if ('status' in error || 'statusCode' in error || 'errorCode' in error) {
    return error;
  }
  return null;
};
const getStatusCode = error => {
  if ('statusCode' in error && typeof error.statusCode === 'number') {
    return error.statusCode;
  }
  if ('status' in error && typeof error.status === 'number') {
    return error.status;
  }
  return undefined;
};
const hasGraphQLErrors = error => 'graphQLErrors' in error && Array.isArray(error.graphQLErrors) && error.graphQLErrors.length > 0;
const isAbortError = error => 'name' in error && error.name === 'AbortError';

/* Single source of truth for "was this request aborted?". Aborted CHIRP
 * requests surface with status 0 ("failed with status 0. Request aborted")
 * when the page is torn down before the query resolves (navigation, refresh,
 * a bot or acceptance test leaving early); they are not real failures.
 * hub-http reports every XHR failure as status 0 and tells them apart with
 * errorCode: 'ABORT' (the abort event), 'TIMEOUT' and 'NETWORKERROR' (the
 * error event: offline, DNS, CORS, blocked). Only ABORT is a teardown, so the
 * errorCode is checked before falling back to status. A bare status 0 with no
 * errorCode (not from hub-http's XHR handlers) is still treated as aborted.
 * A network error with no status is deliberately NOT treated as aborted: real
 * network errors with non-CHIRP bodies land here without one. */
export const getRequestErrorType = error => {
  const requestError = getRequestError(error);
  if (isAbortError(requestError !== null && requestError !== void 0 ? requestError : error)) {
    return 'aborted';
  }
  if (!requestError) {
    return hasGraphQLErrors(error) ? 'graphql' : 'render';
  }
  const errorCode = 'errorCode' in requestError ? requestError.errorCode : undefined;
  if (errorCode === 'ABORT') {
    return 'aborted';
  }
  if (errorCode === 'TIMEOUT') {
    return 'timeout';
  }
  if (errorCode === 'NETWORKERROR') {
    return 'network';
  }
  const statusCode = getStatusCode(requestError);
  if (statusCode === 0) {
    return 'aborted';
  }
  if (statusCode !== undefined) {
    return `http_${statusCode}`;
  }
  return 'network';
};